import { ICompany } from './company';
import { IOrderWithCourseItem } from './order';
import { IPayment } from './payment';
import { ITeam } from './team';
import { IUser, IUserWithProfile, IUserWithTeam } from './user';

export enum LearningBudgetTransactionType {
  CREDIT = 'credit',
  DEBIT = 'debit',
}

export enum LearningBudgetTransactionStatus {
  PENDING = 'pending',
  COMPLETE = 'complete',
  FAILED = 'failed',
}

export enum LearningBudgetTransactionLabel {
  SYSTEM = 'system',
  ORDER = 'order',
  REFUND = 'refund',
}

export interface ILearningBudgetTransaction {
  _id: string;
  type: LearningBudgetTransactionType;
  amount: number;
  amountAvailableAtTransaction: number;
  user: string;
  company: string;
  order?: string;
  payment?: string;
  status: LearningBudgetTransactionStatus;
  labels?: LearningBudgetTransactionLabel[];
  createdAt: string;
  updatedAt: string;
}

export interface ILearningBudgetTransactionWithUser
  extends Omit<ILearningBudgetTransaction, 'user'> {
  user: IUserWithTeam & IUserWithProfile;
}

export interface ILearningBudgetTransactionWithCompany
  extends Omit<ILearningBudgetTransaction, 'company'> {
  company: ICompany;
}

export interface ILearningBudgetTransactionWithPayment
  extends Omit<ILearningBudgetTransaction, 'payment'> {
  payment: IPayment;
}

export interface ILearningBudgetTransactionWithOrder
  extends Omit<ILearningBudgetTransaction, 'order'> {
  order: IOrderWithCourseItem;
}

export interface ILearningBudgetTransactionWithPayment
  extends Omit<ILearningBudgetTransaction, 'payment'> {
  payment: IPayment;
}

export type ILearningBudgetTransactionFilters = Partial<
  Omit<ILearningBudgetTransaction, 'status'>
> & {
  user?: string;
  userName?: string;
  team?: string | string[];
  company?: string;
  digest?: boolean;
  year?: number;
  status?: LearningBudgetTransactionStatus | LearningBudgetTransactionStatus[];
};

export type ILearningBudgetTransactionInTheList = Omit<
  ILearningBudgetTransaction,
  'user' | 'order'
> & {
  user: IUserWithProfile;
  order: IOrderWithCourseItem;
  team: Pick<ITeam, '_id' | 'name'>;
};

export type ApiLearningBudgetTransactionsDigestResponse = {
  metadata: {
    [key in keyof ILearningBudgetTransactionFilters]: unknown;
  };
  data: {
    totalLearningBudget: number;
    usedLearningBudget: number;
    remainingLearningBudget: number;
    uniqueEmployeeCount: number;
  };
};

export enum LearningBudgetPeriodType {
  Annual = 'annual',
}

export enum LearningBudgetSetupStatus {
  Active = 'active',
  Draft = 'draft',
  Expired = 'expired',
  Inactive = 'inactive',
  Scheduled = 'scheduled',
}

export enum LearningBudgetSetupInclude {
  Company = 'company',
  Employee = 'employee',
  Team = 'team',
}

export interface IAmount {
  amount: number;
  currency: string;
}

export interface ITeamOverride {
  team: string;
  budget: IAmount;
}

export interface ITeamOverrideWithTeam extends Omit<ITeamOverride, 'team'> {
  team: Pick<ITeam, 'name' | '_id'>;
}

export interface IEmployeeOverride {
  employee: string;
  budget: IAmount;
}

export interface IEmployeeOverrideWithEmployee
  extends Omit<IEmployeeOverride, 'employee'> {
  employee: Pick<IUser, 'firstName' | 'lastName' | '_id' | 'team'>;
}

export interface ILearningBudgetSetup {
  _id: string;
  company: string;
  status: LearningBudgetSetupStatus;
  periodType: LearningBudgetPeriodType;
  startDate: string;
  endDate: string;
  carryOver: boolean;
  defaultBudget: IAmount;
  teamOverrides?: ITeamOverride[];
  employeeOverrides?: IEmployeeOverride[];
  createdAt: string;
  updatedAt: string;
}

export type ILearningBudgetCreatePayload = Pick<
  ILearningBudgetSetup,
  'startDate' | 'defaultBudget' | 'status'
>;

export type ILearningBudgetUpdatePayload = Pick<
  Partial<ILearningBudgetSetup>,
  | 'company'
  | 'status'
  | 'startDate'
  | 'carryOver'
  | 'defaultBudget'
  | 'teamOverrides'
  | 'employeeOverrides'
>;

export interface ILearningBudgetSetupInTheList
  extends Omit<ILearningBudgetSetup, 'teamOverrides' | 'employeeOverrides'> {
  teamOverrides: ITeamOverrideWithTeam[];
  employeeOverrides: IEmployeeOverrideWithEmployee[];
}
